import { ICON_CLOSE } from '../constants/icons';
import { div, button, anchor, createElement } from "../utils/dom";
import { stripHtml } from '../utils/stripHtml';
import type { Location, MapplicLocalization } from "../types";


interface TooltipCallbacks {
  onClose: () => void;
  getColor: (location: Location) => string | null;
}

export interface TooltipOptions {
  linkNewTab: boolean;
}

const CLASSNAME_TOOLTIP = "mapplic-tooltip";
const CLASSNAME_WRAP = "mapplic-tooltip__wrap";
const CLASSNAME_CLOSE = "mapplic-tooltip__close";
const CLASSNAME_IMAGE = "mapplic-tooltip__image";
const CLASSNAME_TITLE = "mapplic-tooltip__title";
const CLASSNAME_CONTENT = "mapplic-tooltip__content";
const CLASSNAME_DESC = "mapplic-tooltip__description";
const CLASSNAME_LINK = "mapplic-tooltip__link";

const ARIA_LABEL_CLOSE = "Schließen";


export class Tooltip {
  public readonly rootElem: HTMLElement;
  private closeBtn: HTMLButtonElement;
  private image: HTMLImageElement;
  private title: HTMLElement;
  private content: HTMLElement;
  private desc: HTMLElement;
  private link: HTMLAnchorElement;

  // State
  private location: Location | null = null;
  private triggerElement: Element | null = null;

  constructor(
    private options: TooltipOptions,
    private l10n: MapplicLocalization,
    private callbacks: TooltipCallbacks,
  ) {
    this.rootElem = this.createTooltip();
    this.closeBtn = this.rootElem.querySelector(`.${CLASSNAME_CLOSE}`)!;
    this.image = this.rootElem.querySelector(`.${CLASSNAME_IMAGE}`)!;
    this.title = this.rootElem.querySelector(`.${CLASSNAME_TITLE}`)!;
    this.content = this.rootElem.querySelector(`.${CLASSNAME_CONTENT}`)!;
    this.desc = this.rootElem.querySelector(`.${CLASSNAME_DESC}`)!;
    this.link = this.rootElem.querySelector(`.${CLASSNAME_LINK}`)!;

    this.bindEvents();
    this.bindTransitionEnd();
  }

  private createTooltip(): HTMLElement {
    const titleId = `${CLASSNAME_TITLE}-${Math.random().toString(36).substr(2, 9)}`;
    const tooltip = div({
      className: CLASSNAME_TOOLTIP,
      attributes: {
        role: "dialog",
        "aria-labelledby": titleId,
        "aria-modal": "false",
      },
    });

    const wrap = div({ className: CLASSNAME_WRAP });
    tooltip.appendChild(wrap);

    // Close button
    const closeBtn = button({
      className: CLASSNAME_CLOSE,
      attributes: {
        type: "button",
        "aria-label": ARIA_LABEL_CLOSE,
      },
      html: ICON_CLOSE
    });
    wrap.appendChild(closeBtn);

    // Content container
    const content = div({ className: CLASSNAME_CONTENT });
    wrap.appendChild(content);

    // Image
    const image = createElement("img", {
      className: CLASSNAME_IMAGE,
      attributes: { alt: "" },
      style: { display: "none" },
    });
    content.appendChild(image);

    // Title
    const title = div({
      attributes: { id: titleId },
      className: CLASSNAME_TITLE,
    });
    content.appendChild(title);

    // Description
    const desc = div({ className: CLASSNAME_DESC });
    content.appendChild(desc);

    // Link
    const link = anchor({
      className: CLASSNAME_LINK,
      text: this.l10n.more,
      attributes: { href: "#" },
      style: { display: "none" },
    });
    if (this.options.linkNewTab) {
      link.setAttribute("target", "_blank");
    }
    content.appendChild(link);

    return tooltip;
  }

  private bindEvents(): void {
    // Close button click
    this.closeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.closeAndReturn();
    });

    // Touch end on close button (for mobile)
    this.closeBtn.addEventListener("touchend", (e) => {
      e.preventDefault();
      this.closeAndReturn();
    });

    // ESC key to close
    this.rootElem.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.closeAndReturn();
      }
    });
  }

  /**
   * Listen for the end of the hide transition to clean up state.
   * Replaces the manual setTimeout that was previously in NwzMapplic.
   */
  private bindTransitionEnd(): void {
    this.rootElem.addEventListener('transitionend', (e) => {
      // Only react to opacity transitions on the tooltip itself (not children)
      if (e.target !== this.rootElem || e.propertyName !== 'opacity') return;
      // Only clean up when hiding (no data-visible attribute)
      if (!this.rootElem.hasAttribute('data-visible')) {
        this.clear();
      }
    });
  }

  /**
   * Show the tooltip. When `instant` is true, the translate/transform
   * transition is suppressed so the tooltip appears at its position
   * without sliding from a previous location.
   */
  public show(instant: boolean = false): void {
    if (instant) {
      this.rootElem.setAttribute('data-no-motion', '');
    }
    this.rootElem.setAttribute('data-visible', '');
    if (instant) {
      // Force reflow so the browser captures the no-motion state,
      // then re-enable transitions for subsequent moves
      this.rootElem.offsetHeight;
      this.rootElem.removeAttribute('data-no-motion');
    }
  }

  /**
   * Hide the tooltip with a fade-out transition.
   * CSS handles display: none after the transition ends.
   * State cleanup happens in the transitionend listener.
   */
  public hide(): void {
    this.rootElem.removeAttribute('data-visible');
  }

  /**
   * Close tooltip and return focus to trigger element
   */
  public closeAndReturn(): void {
    const returnFocus = this.triggerElement;
    this.callbacks.onClose();

    // Return focus to trigger element
    if (returnFocus && 'focus' in returnFocus) {
      requestAnimationFrame(() => {
        (returnFocus as HTMLElement).focus();
      });
    }
  }

  /**
   * Set the trigger element for focus return
   * Call this before show() when opening from sidebar
   */
  public setTriggerElement(element: Element | null): void {
    this.triggerElement = element;
  }

  /**
   * Show tooltip for a location
   */
  public setLocation(location: Location): void {
    if (this.location && this.location.id === location.id) {
      return;
    }

    this.location = location;

    // If no explicit trigger was set, use location.el as fallback
    // if (!this.triggerElement) {
    //   this.triggerElement = location.elem || null;
    // }

    // Image
    if (location.image) {
      this.image.src = "";
      this.image.src = location.image;
      this.image.alt = location.title;
      this.image.style.display = "";
    } else {
      this.image.alt = "";
      this.image.style.display = "none";
    }

    // Link
    if (location.link) {
      this.link.href = location.link;
      this.link.style.display = "";
    } else {
      this.link.style.display = "none";
    }

    // Title
    this.title.textContent = location.title;

    // Description
    const cleanDescriptionText = location.description
      ? stripHtml(location.description).toLowerCase()
      : '';

    const cleanTitleText = location.title.replace(/\*+$/, '').trim().toLowerCase();
    if (location.description && cleanDescriptionText !== cleanTitleText) {
      this.desc.innerHTML = location.description;
      this.desc.style.display = "";
    } else {
      this.desc.innerHTML = "";
      this.desc.style.display = "none";
    }

    // Reset scroll
    this.content.scrollTop = 0;

    // Focus on close button for accessibility
    // when the tooltip is opened programmatically (e.g. via URL)
    // triggerElement is set to null, so we can skip the focus
    requestAnimationFrame(() => {
      if (this.triggerElement ) {
        this.closeBtn.focus({ preventScroll: true });
      }
      });
  }

  /**
   * Clear tooltip
   */
  public clear(): void {
    this.location = null;
    this.triggerElement = null;
    this.desc.innerHTML = "";
  }

  /**
   * Position tooltip at content-pixel coordinates.
   * Uses CSS `translate` for GPU-composited animation.
   */
  public setPosition(px: number, py: number): void {
    this.rootElem.style.translate = `${px}px ${py}px`;
  }

  /**
   * Whether the tooltip is currently visible
   */
  public isVisible(): boolean {
    return this.rootElem.hasAttribute('data-visible');
  }


  public getDimensions(): { width: number; height: number } {
    const style = window.getComputedStyle(this.rootElem);
    const marginTop = parseFloat(style.marginTop) || 0;
    const marginBottom = parseFloat(style.marginBottom) || 0;
    const marginLeft = parseFloat(style.marginLeft) || 0;
    const marginRight = parseFloat(style.marginRight) || 0;
    return {
      width: this.rootElem.offsetWidth + marginLeft + marginRight,
      height: this.rootElem.offsetHeight + marginTop + marginBottom,
    };
  }

  /**
   * Get current location
   */
  public getLocation(): Location | null {
    return this.location;
  }
}
